import React, { useEffect, useState, useRef } from "react";

import { useWsNavigate } from "../hooks/useWsNavigate";
import { usePageTitle } from "../contexts/PageTitleContext";
import { useAuth } from "../contexts/AuthContext";
import { useAgent } from "../contexts/AgentContext";
import { useFeature } from "../hooks/useFeature";
import { useOnboarding } from "../contexts/OnboardingContext";
import OnboardingViewerEmptyState from "../components/onboarding/OnboardingViewerEmptyState";
import { ONBOARDING_STEPS } from "../components/onboarding/steps";
import DashboardActivity from "../components/dashboard/DashboardActivity";
import DiscoveryCarousel from "../components/dashboard/DiscoveryCarousel";
import {
  scenariosAPI,
  callObservationsAPI,
} from "../services/api";
import {
  Bot,
  FileText,
  Layers,
  CheckCircle2,
  Plus,
  ArrowRight,
  MessageSquare,
  Zap,
  PhoneOff,
  AlertTriangle,
  BarChart2,
  Upload,
  FlaskConical,
  Sparkles,
  Code,
} from "lucide-react";

// ─── FTUX: No Agent ─────────────────────────────────────────────────────────

const NoAgentState = ({ navigate, userName }) => (
  <div className="space-y-6">
    {/* Hero — soft gradient, not a slab of black */}
    <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-muted/70 via-background to-primary/[0.06] p-6 md:p-8">
      <div className="absolute right-0 top-0 h-64 w-64 -translate-y-1/3 translate-x-1/3 rounded-full bg-foreground/[0.03]" />
      <div className="relative">
        <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Welcome{userName ? `, ${userName}` : ""}
        </p>
        <h1 className="mb-3 text-2xl font-bold text-foreground md:text-3xl">
          Let's set up your first Test agent
        </h1>
        <p className="max-w-lg text-sm leading-relaxed text-muted-foreground">
          Before you can test, evaluate, or monitor anything, tell us which AI
          agent you're working with — then we'll walk you through the rest.
        </p>
      </div>
    </div>

    {/* Roadmap — small step cards in a row + primary CTA */}
    <div>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            Get your agent under test in {ONBOARDING_STEPS.length} steps
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Start with your agent — each step unlocks the next.
          </p>
        </div>
        <button
          onClick={() => navigate("/ai-testing-agent/create")}
          className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Create Your First Agent
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
        {ONBOARDING_STEPS.map((step, i) => (
          <button
            key={step.id}
            onClick={() => navigate(step.cta.href)}
            className="group rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-input hover:bg-muted/30"
          >
            <div className="mb-3 flex items-center justify-between">
              <step.icon
                className="h-4 w-4 text-muted-foreground"
                strokeWidth={1.75}
              />
              <span className="text-xs font-semibold text-muted-foreground/60">
                {i + 1}
              </span>
            </div>
            <p className="text-sm font-medium leading-snug text-foreground">
              {step.title}
            </p>
            <div className="mt-1 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{step.estTime}</p>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
          </button>
        ))}
      </div>
    </div>

    {/* Why teams test — quiet info, not a product surface */}
    <div className="border-t border-border pt-5">
      <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
        Why teams test their agents
      </p>
      <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-3">
        {[
          {
            icon: PhoneOff,
            title: "Catch dropped calls",
            desc: "Failed transfers, unexpected hang-ups, dead air.",
          },
          {
            icon: AlertTriangle,
            title: "Track quality over time",
            desc: "See when a prompt change quietly breaks something.",
          },
          {
            icon: MessageSquare,
            title: "Test before customers do",
            desc: "Refund disputes, angry callers, edge cases — on your terms.",
          },
        ].map((item) => (
          <div key={item.title} className="flex gap-2.5">
            <item.icon
              className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground/70"
              strokeWidth={1.75}
            />
            <div>
              <p className="text-sm font-medium text-foreground">{item.title}</p>
              <p className="text-xs leading-relaxed text-muted-foreground">
                {item.desc}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─── Example issues (subtle footer) ─────────────────────────────────────────

const EXAMPLE_ISSUES = [
  {
    agent: "Your order was delivered yesterday and signed by John.",
    tag: "Hallucination",
    tagColor: "text-red-500",
  },
  {
    agent: "I'm sorry, I'm unable to complete the transfer right now.",
    tag: "Failed transfer",
    tagColor: "text-amber-500",
  },
  {
    agent: "I'd recommend disputing the charge with your bank.",
    tag: "Off-script",
    tagColor: "text-purple-500",
  },
];

// ─── No Activity State ──────────────────────────────────────────────────────

const NoActivityState = ({ navigate, scenarioCount, conversationCount, firstName }) => {
  const hasScenarios = scenarioCount > 0;
  const hasConversations = conversationCount > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-foreground">
          {firstName ? `You're set up, ${firstName}` : "You're set up"} — here's what's next
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Test your agent against tricky scenarios, or connect production calls to
          catch issues automatically.
        </p>
      </div>

      {/* Two paths */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-card rounded-xl border border-border p-4 flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${hasScenarios ? "bg-emerald-50" : "bg-violet-50"}`}
            >
              {hasScenarios ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              ) : (
                <FlaskConical className="h-5 w-5 text-violet-500" />
              )}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                {hasScenarios
                  ? `${scenarioCount} scenarios ready`
                  : "Simulate test conversations"}
              </h3>
              <p className="text-sm text-muted-foreground">
                {hasScenarios
                  ? "Run them as a batch to evaluate your agent"
                  : "Our AI caller will test your agent for you"}
              </p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">
            {hasScenarios
              ? "Your scenarios are ready. Launch a batch test to see how your agent handles each one and measure pass/fail rates."
              : "Create scenarios that mirror real conversations — refund requests, transfers, edge cases. We'll call your agent and tell you exactly where it breaks."}
          </p>
          <button
            onClick={() =>
              navigate(hasScenarios ? "/batch-run/new" : "/live-simulations/scenario")
            }
            className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors w-fit"
          >
            {hasScenarios ? (
              <>
                <Zap className="h-4 w-4" /> Run Batch Test
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" /> Create Test Scenarios
              </>
            )}
          </button>
        </div>

        <div className="bg-card rounded-xl border border-border p-4 flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${hasConversations ? "bg-emerald-50" : "bg-emerald-50"}`}
            >
              {hasConversations ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              ) : (
                <MessageSquare className="h-5 w-5 text-emerald-500" />
              )}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                {hasConversations
                  ? `${conversationCount} conversations analyzed`
                  : "Evaluate real production calls"}
              </h3>
              <p className="text-sm text-muted-foreground">
                {hasConversations
                  ? "Keep monitoring to track agent health"
                  : "Connect your calls for automatic analysis"}
              </p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">
            {hasConversations
              ? "Keep feeding production calls to catch regressions and maintain quality."
              : "Send call recordings via API and we'll automatically transcribe, evaluate, and flag issues. Set it up once — every call gets analyzed."}
          </p>
          {hasConversations ? (
            <button
              onClick={() => navigate("/observability/conversations")}
              className="flex items-center gap-2 px-4 py-2.5 bg-card text-foreground text-sm font-medium rounded-lg border border-border hover:bg-muted/50 transition-colors w-fit"
            >
              <BarChart2 className="h-4 w-4" /> View Conversations
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/settings/api_keys")}
                className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Code className="h-4 w-4" /> Connect via API
              </button>
              <button
                onClick={() => navigate("/observability/conversations")}
                className="flex items-center gap-2 px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <Upload className="h-4 w-4" /> Upload Calls
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Secondary actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          {
            label: "Set up metrics",
            desc: "Define what good looks like",
            href: "/evaluation/metrics",
            icon: BarChart2,
          },
          {
            label: "Optimize prompts",
            desc: "Diagnose gaps and improve prompts",
            href: "/prompt-optimizer",
            icon: Sparkles,
          },
          {
            label: "Add another agent",
            desc: "Test multiple agents",
            href: "/ai-testing-agent/create",
            icon: Plus,
          },
        ].map((item) => (
          <button
            key={item.label}
            onClick={() => navigate(item.href)}
            className="bg-card rounded-xl border border-border p-4 flex items-center gap-3 hover:border-input transition-colors text-left group"
          >
            <div className="w-9 h-9 bg-muted/50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-muted transition-colors">
              <item.icon className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{item.label}</p>
              <p className="text-sm text-muted-foreground">{item.desc}</p>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground/40 ml-auto group-hover:text-muted-foreground transition-colors" />
          </button>
        ))}
      </div>

      {/* What evaluation catches */}
      <div className="rounded-xl border border-border bg-muted/30 p-5">
        <p className="text-sm font-medium text-foreground mb-3">
          The kind of issues evaluation catches
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {EXAMPLE_ISSUES.map((item, i) => (
            <div
              key={i}
              className="rounded-lg border border-border bg-card p-3"
            >
              <span
                className={`text-[10px] font-semibold uppercase tracking-wide ${item.tagColor}`}
              >
                {item.tag}
              </span>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                “{item.agent}”
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Main Dashboard ─────────────────────────────────────────────────────────

const Dashboard = () => {
  const { setPageTitle } = usePageTitle();
  const { user, currentWorkspace } = useAuth();
  const { agents, agentsLoading } = useAgent();
  const navigate = useWsNavigate();
  const canAiGenerate = useFeature("scenarios.ai_scenario_generation");
  const {
    showOnboarding,
    isViewer,
    loading: onboardingLoading,
    refresh: refreshOnboarding,
  } = useOnboarding();

  const [batchCounts, setBatchCounts] = useState({ total: 0, completed: 0 });
  const [scenarioCount, setScenarioCount] = useState(0);
  const [conversationCount, setConversationCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPageTitle("Dashboard");
    return () => setPageTitle(null);
  }, [setPageTitle]);

  useEffect(() => {
    if (user && currentWorkspace) fetchStats();
  }, [user, currentWorkspace]); // eslint-disable-line react-hooks/exhaustive-deps

  // Landing on / returning to the Dashboard re-syncs onboarding progress so
  // steps a user just completed (scenario, batch, connected call) check off.
  useEffect(() => {
    refreshOnboarding?.();
  }, [refreshOnboarding]);

  // Writers with incomplete onboarding are sent to the dedicated /onboarding
  // route. Waiting for the provider to finish loading before deciding avoids the
  // flicker of dashboard content painting first.
  useEffect(() => {
    if (!onboardingLoading && showOnboarding) {
      navigate("/onboarding", { replace: true });
    }
  }, [onboardingLoading, showOnboarding, navigate]);

  const fetchStats = async () => {
    try {
      const [batchesRes, scenariosRes, convoRes] = await Promise.allSettled([
        scenariosAPI.getBatchesWithPagination(1, currentWorkspace.id),
        scenariosAPI.getScenarios(currentWorkspace.id),
        callObservationsAPI.getCallObservations(currentWorkspace.id, {
          per_page: 1,
        }),
      ]);
      if (batchesRes.status === "fulfilled") {
        const counts = batchesRes.value?.data?.counts;
        if (counts)
          setBatchCounts({ total: counts.total, completed: counts.completed });
      }
      if (scenariosRes.status === "fulfilled") {
        const data = scenariosRes.value?.data;
        setScenarioCount(
          Array.isArray(data) ? data.length : data?.data?.length || 0,
        );
      }
      if (convoRes.status === "fulfilled") {
        const meta = convoRes.value?.data?.meta;
        setConversationCount(meta?.total_count || 0);
      }
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  // Wait for onboarding state before deciding what to show, and render a spinner
  // (not dashboard content) while a writer is being redirected to /onboarding.
  if (!user || agentsLoading || loading || onboardingLoading || showOnboarding) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  const agentCount = agents.length;
  const hasActivity = batchCounts.total > 0 || conversationCount > 0;

  // Viewer on an empty workspace → waiting-for-setup message (viewers never
  // onboard). Writers' onboarding lives on the dedicated /onboarding route.
  if (isViewer && agentCount === 0) {
    return (
      <div className="px-4">
        <OnboardingViewerEmptyState />
      </div>
    );
  }

  // ── FTUX: No agents ──
  if (agentCount === 0) {
    return (
      <div className="px-4">
        <NoAgentState
          navigate={navigate}
          userName={user?.name?.split(" ")[0]}
        />
      </div>
    );
  }

  // ── FTUX: Has agents, no activity ──
  if (!hasActivity) {
    return (
      <div className="px-4">
        <NoActivityState
          navigate={navigate}
          scenarioCount={scenarioCount}
          conversationCount={conversationCount}
          firstName={user?.name?.split(" ")[0]}
        />
      </div>
    );
  }

  // ── Active dashboard ──
  return (
    <div className="px-4 space-y-6">
      {/* Quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          {
            label: "New Agent",
            desc: "Add an agent to test",
            href: "/ai-testing-agent/create",
            icon: Bot,
            bg: "bg-violet-50",
            color: "text-violet-500",
          },
          {
            label: "Run Batch Test",
            desc: "Test scenarios at scale",
            href: "/batch-run/new",
            icon: Zap,
            bg: "bg-blue-50",
            color: "text-blue-500",
          },
          ...(canAiGenerate
            ? [
                {
                  label: "Generate Scenarios by AI",
                  desc: "Create test scenarios with AI",
                  href: "/live-simulations/generate",
                  icon: FlaskConical,
                  bg: "bg-emerald-50",
                  color: "text-emerald-500",
                },
              ]
            : []),
          {
            label: "Conversations",
            desc: "Monitor production calls",
            href: "/observability/conversations",
            icon: MessageSquare,
            bg: "bg-amber-50",
            color: "text-amber-500",
          },
        ].map((item) => (
          <button
            key={item.label}
            onClick={() => navigate(item.href)}
            className="bg-card rounded-xl border border-border p-4 flex items-center gap-3 hover:border-input transition-colors text-left group"
          >
            <div
              className={`w-10 h-10 ${item.bg} rounded-lg flex items-center justify-center flex-shrink-0`}
            >
              <item.icon className={`h-5 w-5 ${item.color}`} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                {item.label}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {item.desc}
              </p>
            </div>
          </button>
        ))}
      </div>

      {/* Discovery carousel */}
      <DiscoveryCarousel clientId={currentWorkspace?.id} />

      {/* Activity & health */}
      <DashboardActivity clientId={currentWorkspace?.id} navigate={navigate} />
    </div>
  );
};

export default Dashboard;
